import React, { FormEvent } from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { FieldControl, FieldGroup, FormBuilder, Validators } from 'react-reactive-form';
import { Spinner } from 'reactstrap';
import { ThunkDispatch } from 'redux-thunk';
import {
  CreateUserRequest,
  UserModel,
  UserPermissionGroupModel,
  UserRequest,
  UserResponse
} from '../../../../api/authentication/authentication.types';
import { DATE_TIME_FORMAT, SelectedOption } from '../../../../api/api.types';
import Select from '../../../../components/forms/Select';
import { AppState } from '../../../../store/store.types';
import { TextInput } from '../../../../components/forms/Input';
import Breadcrumbs from '../../../../components/breadcrumbs/Breadcrumbs';
import ConfirmationModal from '../../../../components/modals/ConfirmationModal';
import { AuthenticationApi } from '../../../../api/api.apiclient';
import { AuthenticationActionTypes } from '../../../authentication/types/authentication.types';
import { performLogout } from '../../../authentication/actions/authentication.action';
import dayjs from 'dayjs';
import { WithTranslation, withTranslation } from 'react-i18next';
import { SupplierModel } from '../../../../api/admin/admin.types';

interface UserLocationProps {
  userLogin: string;
}

interface UserDetailProps extends RouteComponentProps<UserLocationProps>, WithTranslation {
  user: UserModel | null;
  suppliers: SupplierModel[];
  isNew?: boolean;
  performLogout: () => void;
}

interface UserDetailsState {
  selectedUser: UserResponse | null;
  userPermissions: UserPermissionGroupModel[];
  isModalOpen: boolean;
  isEdit: boolean;
  awaitingResponse: boolean;
}

const emailPatternRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

class UserDetails extends React.Component<UserDetailProps, UserDetailsState> {

  readonly state = {
    selectedUser: null,
    userPermissions: [] as UserPermissionGroupModel[],
    isModalOpen: false,
    isEdit: false,
    awaitingResponse: false
  };

  form = FormBuilder.group({
    userPermissionGroupCode: [ null, Validators.required ],
    email: [ null, Validators.pattern(emailPatternRegex) ],
    username: [ null, Validators.required ],
    password: [ null, Validators.minLength(8) ],
    firstName: [ null ],
    lastName: [ null ],
    suppliers: [ [] ]
  });

  componentDidMount() {
    const { userLogin } = this.props.match.params;

    AuthenticationApi.user.getPermissionGroups().then((response) => {
      this.setState({ userPermissions: response });
    });

    if (userLogin && !this.props.isNew) {
      AuthenticationApi.user.getUserByEmail(userLogin).then((response) => {
        this.setState({ selectedUser: response });
        this.updateFormValue();
      });
    }
  }

  updateFormValue = () => {
    if (!this.state.selectedUser) {
      return;
    }
    const selectedUser = this.state.selectedUser as unknown as UserResponse;

    this.form.patchValue({
      userPermissionGroupCode: this.state.userPermissions.find((p) => p.name === selectedUser.userPermissionGroupName),
      email: selectedUser.email,
      username: selectedUser.username,
      firstName: selectedUser.firstName,
      lastName: selectedUser.lastName,
      suppliers: selectedUser.suppliers.map((s) => ({ value: s.code, label: s.name }))
    });
  };

  patchField = (option: SelectedOption, field: string) => {
    this.form.patchValue({ [ field ]: option.value });
  };

  onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const form = this.form.controls;
    this.form.markAsSubmitted();
    if (this.form.invalid) {
      return;
    }

    this.setState({ awaitingResponse: true });

    const request: UserRequest = {
      email: form.email.value,
      username: form.username.value,
      password: form.password.value,
      firstName: form.firstName.value,
      lastName: form.lastName.value
    };

    const suppliers: string[] = [];
    form.suppliers.value.forEach((sp: { value: string; label: string }) => {
      suppliers.push(sp.value);
    });

    const createRequest: CreateUserRequest = {
      user: request,
      supplierCodes: suppliers,
      userPermissionGroupCode: form.userPermissionGroupCode.value.code ? form.userPermissionGroupCode.value.code : form.userPermissionGroupCode.value
    };

    const { userLogin } = this.props.match.params;
    if (this.props.isNew) {
      AuthenticationApi.user.createUser(createRequest).then((response) => {
        this.props.history.push(`/admin/users/${response.username}/view`);
      }).finally(() => this.setState({ awaitingResponse: false }));
    } else if (userLogin) {
      AuthenticationApi.user.editUser(createRequest, userLogin).then((response) => {
        if (this.props.user && this.props.user.username === userLogin) {
          this.props.performLogout();
        } else {
          this.setState({ isEdit: false });
        }
      }).finally(() => this.setState({ awaitingResponse: false }));
    }
  };

  onCancel = () => {
    this.props.history.push('/admin/users');
  };

  onEdit = () => {
    this.setState({ isEdit: true });
  };

  getUserPermissionGroupValue = () => {
    if (this.form.controls.userPermissionGroupCode.value) {
      const selectedPermissionGroup = this.state.userPermissions.find((select) => select.code === this.form.controls.userPermissionGroupCode.value.code);
      if (selectedPermissionGroup) return { value: selectedPermissionGroup.code, label: selectedPermissionGroup.name };
    }

    return undefined;
  };

  getSuppliers = (suppliers: SupplierModel[]) => {
    const supplierList = (
      <ul className="details-list">
        {suppliers.map((supplier: SupplierModel) => (
          <li key={supplier.code}>{supplier.name}</li>
        ))}
      </ul>
    );

    return supplierList;
  };

  onSuppliersChange = (values: any) => {
    this.form.patchValue({ suppliers: values });
  };

  onDelete = () => {
    const { userLogin } = this.props.match.params;
    if (userLogin) {
      this.setState({ isModalOpen: false });
      AuthenticationApi.user.deleteUserCommand(userLogin).then((e: any) => this.props.history.push('/users'));
    }
  };

  render() {
    if (!this.props.isNew && !this.state.selectedUser) {
      return null;
    }

    const showForm = this.state.isEdit || this.props.isNew;
    const selectedUser = this.state.selectedUser as unknown as UserResponse;
    const { t } = this.props;
    return (
      <main>
        <Breadcrumbs
          items={
            [
              { path: '/admin', label: 'Admin' },
              { path: '/admin/users', label: 'Users' },
              {
                path: null,
                label: this.props.isNew ? 'New user' : selectedUser && String(selectedUser.username) || ''
              }
            ]
          }
        />
        <div className="page-content">
          <div className="row justify-content-end">
            <div className="col-12 d-flex align-items-center justify-content-between">
              <h1 className="mb-0">{this.props.isNew ? t('page_title.add_new_user') :
                t('page_title.user_details')}</h1>
              {this.props.user && selectedUser && !showForm && (
                <div>
                  {this.props.user && selectedUser && selectedUser.username !== this.props.user.username && (
                    <button type="button" className="btn btn-danger mr-2"
                            onClick={() => this.setState({ isModalOpen: true })}>
                      {t('b.delete_Generic delete button').toUpperCase()}</button>
                  )}
                  <button type="button" className="btn btn-primary edit-button"
                          onClick={() => this.onEdit()}>
                    {t('b.edit_Generic edit button').toUpperCase()}
                  </button>
                </div>
              )}
            </div>
          </div>
          <FieldGroup
            strict={false}
            control={this.form}
            render={() => (
              <form className="form-horizontal" onSubmit={(e) => this.onSubmit(e)}>
                <hr/>
                <div className="form-group row">
                  <div className="col-4 d-flex align-items-center justify-content-end ">
                    {showForm && (
                      <label className="mandatory-field">* </label>
                    )}
                    <label>{t('l.user.username')}:</label>
                  </div>
                  <div className="col-2 d-flex align-items-center justify-content-start">
                    {
                      selectedUser && !showForm
                      && (
                        <div className="form-control-static">
                          {this.form.controls.username.value}
                        </div>
                      )
                    }
                    {
                      showForm
                      && (
                        <FieldControl
                          name="username"
                          render={TextInput}
                        />
                      )
                    }
                  </div>
                </div>
                <div className="form-group row">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                    <label>{t('l.user.email')}:</label>
                  </div>
                  <div className="col-2 d-flex align-items-center justify-content-start">
                    {
                      selectedUser && !showForm
                      && (
                        <div className="form-control-static">
                          {this.form.controls.email.value}
                        </div>
                      )
                    }
                    {
                      showForm
                      && (
                        <FieldControl
                          name="email"
                          render={TextInput}
                        />
                      )
                    }
                  </div>
                </div>
                {showForm && (
                  <div className="form-group row">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      {showForm && (
                        <label className="mandatory-field">* </label>
                      )}
                      <label>{t('l.user.password')}:</label>
                    </div>
                    <div className="col-2 d-flex align-items-center justify-content-start">
                      <FieldControl
                        name="password"
                        render={TextInput}
                      />
                    </div>
                  </div>
                )}
                <div className="form-group row">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                    {showForm && (
                      <label className="mandatory-field">* </label>
                    )}
                    <label>{t('l.user.user_permission_group')}:</label>
                  </div>
                  <div className="col-2 d-flex align-items-center justify-content-start">
                    {
                      selectedUser && !showForm
                      && (
                        <div className="form-control-static">
                          {selectedUser.userPermissionGroupName}
                        </div>
                      )
                    }
                    {
                      showForm
                      && (
                        <FieldControl
                          strict={false}
                          name="userPermissionGroupCode"
                          render={(props) => (
                            <Select
                              value={this.getUserPermissionGroupValue()}
                              className="mr-1"
                              placeholder={t('ph.user.permission_group')}
                              options={this.state.userPermissions.map((p) => ({ value: p.code, label: p.name }))}
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              disableClear
                              label="user permission group"
                              onChange={(value) => this.patchField(value as SelectedOption, 'userPermissionGroupCode')}
                            />
                          )}
                        />
                      )
                    }
                  </div>
                </div>
                <div className="form-group row">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                    <label>{t('l.user.first_name')}:</label>
                  </div>
                  <div className="col-2 d-flex align-items-center justify-content-start">
                    {
                      selectedUser && !showForm
                      && (
                        <div className="form-control-static">
                          {this.form.controls.firstName.value}
                        </div>
                      )
                    }
                    {
                      showForm
                      && (
                        <FieldControl
                          name="firstName"
                          render={TextInput}
                        />
                      )
                    }
                  </div>
                </div>
                <div className="form-group row">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                    <label>{t('l.user.last_name')}:</label>
                  </div>
                  <div className="col-2 d-flex align-items-center justify-content-start">
                    {
                      selectedUser && !showForm
                      && (
                        <div className="form-control-static">
                          {this.form.controls.lastName.value}
                        </div>
                      )
                    }
                    {
                      showForm
                      && (
                        <FieldControl
                          name="lastName"
                          render={TextInput}
                        />
                      )
                    }
                  </div>
                </div>
                {!showForm && (
                  <div className="form-group row">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      <label>{t('ct.users.last_login')}:</label>
                    </div>
                    <div className="col-2 d-flex align-items-center justify-content-start">
                      {
                        selectedUser && !showForm
                        && (
                          <div className="form-control-static">
                            {dayjs(selectedUser.lastLogin).format(DATE_TIME_FORMAT)}
                          </div>
                        )
                      }
                    </div>
                  </div>
                )}
                <div className="form-group row">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                    {showForm && (
                      <label className="mandatory-field">* </label>
                    )}
                    <label>{t('l.user.suppliers')}:</label>
                  </div>
                  <div className="col-6 d-flex align-items-center justify-content-start">
                    {selectedUser && !showForm && (
                      <div className="form-control-static">
                        {this.getSuppliers(selectedUser.suppliers)}
                      </div>
                    )}
                    {showForm && (
                      <Select
                        value={this.form.controls.suppliers.value}
                        className="mr-1"
                        placeholder={t('ph.user.suppliers')}
                        options={this.props.suppliers.map((s) => (
                          { value: s.code, label: s.name }
                        ))}
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        label="suppliers"
                        onChange={(value) => this.onSuppliersChange(value)}
                      />
                    )}
                  </div>
                </div>
                {showForm && (
                  <div className="row">
                    <div className="col-12 text-right">
                      {!this.state.awaitingResponse ? (
                        <>
                          <button type="button" className="btn btn-outline-secondary btn-lg mr-2"
                                  onClick={this.onCancel}>
                            {t('b.cancel_Generic cancel button translation').toUpperCase()}</button>
                          <button type="submit" className="btn btn-primary btn-lg" onClick={this.onSubmit}>
                            {t('b.save_Generic save button translation').toUpperCase()}</button>
                        </>
                      ) : (
                        <>
                          <Spinner className="mr-1" animation="border" variant="info"/>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </form>
            )}
          />
        </div>
        <ConfirmationModal
          title="Confirm item removal"
          content="Are you sure you want to remove this item?"
          isOpen={this.state.isModalOpen}
          toggle={() => this.setState({ isModalOpen: false })}
          onConfirm={() => this.onDelete()}
          onCancel={() => this.setState({ isModalOpen: false })}
        />
      </main>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  user: state.authentication.user,
  suppliers: state.authentication.suppliers
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<{}, {}, AuthenticationActionTypes>
) => ({
  performLogout: () => dispatch(performLogout())
});

export default withTranslation()(connect(
  mapStateToProps,
  mapDispatchToProps
)(UserDetails));
